<script setup lang="ts">
    import { FilterStoreName } from '@/js/services/api/filter/types';
    import { useFilterStoreByName } from '@/js/stores/filter';
    import { computed } from 'vue';
    import BreadCrumbEllipsis from './BreadCrumbEllipsis.vue';
    import { __ } from '@/js/stores/expressions';
    import { createHierarchicalEchoFilterCheckboxMap } from './BreadCrumb.utils';

    const props = defineProps<{
        selectedPath: string[];
    }>();

    const emit = defineEmits<{
        (e: 'update:selectedPath', value: string[]): void;
    }>();

    const filterStore = useFilterStoreByName(FilterStoreName.SharedFilter);

    const levelMap = computed(() =>
        createHierarchicalEchoFilterCheckboxMap(
            filterStore.all?.levelFilter ? filterStore.all.levelFilter : []
        )
    );

    const breadcrumbNames = computed(() => {
        const names: string[] = [];
        let currentLevelMap = levelMap.value;
        for (const id of props.selectedPath) {
            const node = currentLevelMap[id];
            if (node) {
                names.push(node.name);
                currentLevelMap = node.children;
            } else {
                break;
            }
        }
        return names;
    });

    function goToLevel(idx: number) {
        const newPath = props.selectedPath.slice(0, idx + 1);
        emit('update:selectedPath', newPath);
    }
</script>
<template>
    <div class="flex">
        <p class="text-body">
            <span
                class="cursor-pointer text-body hover:underline"
                :class="{ 'text-body-bold': selectedPath.length === 0 }"
                @click="$emit('update:selectedPath', [])"
            >
                {{ __('organisation-breadcrumb') }}
            </span>
            <template v-if="breadcrumbNames.length < 3">
                <template v-for="(name, idx) in breadcrumbNames" :key="idx">
                    <span>{{ ' / ' }}</span>
                    <span
                        class="cursor-pointer text-body hover:underline"
                        :class="{ 'text-body-bold': selectedPath.length - 1 === idx }"
                        @click="goToLevel(idx)"
                    >
                        {{ name }}
                    </span>
                </template>
            </template>
            <template v-else>
                <span class="text-body">{{ '/' }}</span>
                <BreadCrumbEllipsis>
                    <template #content>
                        <div>
                            <span
                                v-for="(name, idx) in breadcrumbNames.slice(
                                    0,
                                    breadcrumbNames.length - 1
                                )"
                                :key="idx"
                                class="block cursor-pointer hover:underline text-body mb-1"
                                @click="goToLevel(idx)"
                            >
                                {{ name }}
                            </span>
                        </div>
                    </template>
                </BreadCrumbEllipsis>
                <span class="text-body">{{ '/' }}</span>
                <span class="cursor-pointer hover:underline text-body-bold">
                    {{ ' ' + breadcrumbNames[breadcrumbNames.length - 1] }}
                </span>
            </template>
        </p>
    </div>
</template>
